import {
  ShieldCheck,
  KeyRound,
  Lock,
  Database,
  Cpu,
  Clipboard,
  Clock,
  Fingerprint,
  Camera,
  FileLock2,
  ListChecks,
  Check,
  ServerOff,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const pillars = [
  {
    icon: ServerOff,
    title: "Zero-knowledge architecture",
    body: "We don't operate servers that hold your secrets. There is nothing for us to leak, subpoena, or lose.",
  },
  {
    icon: Database,
    title: "Local-only encrypted storage",
    body: "Your vault file lives on your device. Sync — when enabled — moves only the encrypted bundle.",
  },
  {
    icon: Lock,
    title: "AES-256-GCM at rest",
    body: "Every item is sealed individually with authenticated encryption. Tampering is detectable.",
  },
  {
    icon: Cpu,
    title: "Argon2id key derivation",
    body: "Master password is stretched with memory-hard Argon2id (PBKDF2-SHA-512 fallback) before unlocking the vault.",
  },
  {
    icon: KeyRound,
    title: "Master password never stored",
    body: "Your master password is held in protected memory only while the vault is unlocked.",
  },
  {
    icon: Clipboard,
    title: "Clipboard auto-clear",
    body: "Copied secrets are wiped on a configurable timer — and on app blur.",
  },
  {
    icon: Clock,
    title: "Auto-lock",
    body: "Lock on inactivity, app switch, lid close, or screen off.",
  },
  {
    icon: Fingerprint,
    title: "Biometric unlock",
    body: "Touch ID, Face ID, Windows Hello and Android biometrics — never bypass the master key derivation.",
  },
  {
    icon: Camera,
    title: "Screenshot protection",
    body: "Block thumbnails, app-switcher previews and screen recordings.",
  },
  {
    icon: FileLock2,
    title: "Encrypted backup",
    body: "Backups inherit the vault's encryption. Without your master password they're indistinguishable from noise.",
  },
];

const checklist = [
  "Master password is required to unlock — even with biometrics",
  "Auto-lock is enabled by default (5 min)",
  "Clipboard cleared 30s after copy",
  "Screenshots disabled while vault is unlocked",
  "Failed unlock attempts trigger throttling",
  "Decoy vault available under duress",
  "Backup is encrypted with the vault key — not a separate password",
  "No analytics, no telemetry, no crash reports leave the device",
];

export default function Security() {
  return (
    <div>
      <section className="py-20 lg:py-28" data-testid="security-hero">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="label-eyebrow mb-5">Security</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
              Audit-ready by design.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              The threat model that drives AVault: assume the device is trusted, the network is hostile, and the vendor (us) should never need to be.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="glass-card p-8 relative overflow-hidden">
              <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full mint-glow blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2 text-mint-600 dark:text-mint">
                  <ShieldCheck className="h-5 w-5" />
                  <p className="label-eyebrow !text-mint-600 dark:!text-mint">Encryption stack</p>
                </div>
                <ul className="mt-5 space-y-3 text-sm">
                  {[
                    ["Vault sealing", "AES-256-GCM"],
                    ["KDF", "Argon2id (m=64MiB, t=3, p=1)"],
                    ["Random source", "OS CSPRNG"],
                    ["Backup format", "Authenticated, versioned"],
                    ["Memory hardening", "Locked pages, zeroed on lock"],
                  ].map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between gap-4 border-b border-border pb-3 last:border-b-0 last:pb-0">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="font-mono text-xs text-foreground">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="The 10 pillars" title="What we promise. What we don't." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {pillars.map((p) => (
              <div
                key={p.title}
                className="glass-card p-7 hover:-translate-y-1 transition-all duration-300"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="h-11 w-11 rounded-xl bg-mint/40 dark:bg-mint/20 flex items-center justify-center text-mint-700 dark:text-mint">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="checklist-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="glass-card p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -left-24 -bottom-24 h-72 w-72 rounded-full mint-glow blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 text-mint-600 dark:text-mint">
                <ListChecks className="h-5 w-5" />
                <p className="label-eyebrow !text-mint-600 dark:!text-mint">Security checklist</p>
              </div>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tighter">
                Defaults a security team would actually accept.
              </h2>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-4">
                {checklist.map((c, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-foreground/90 leading-relaxed"
                    data-testid={`checklist-item-${i}`}
                  >
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-mint flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-ink" strokeWidth={3} />
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
